if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.deferredImageObserver = null;
        this.handleDeferredImageMediaQueryChange = this.observeDeferredImages.bind(this);
        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.hydrateSlideAndNeighbors(this.elements.viewer?.querySelector('[data-media-id].is-active'));
        this.observeDeferredImages();

        if (this.mql.addEventListener) {
          this.mql.addEventListener('change', this.handleDeferredImageMediaQueryChange);
        } else if (this.mql.addListener) {
          this.mql.addListener(this.handleDeferredImageMediaQueryChange);
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const currentSlide = event.detail.currentElement;
        this.hydrateSlideAndNeighbors(currentSlide);
        if (!this.elements.thumbnails || !currentSlide) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentSlide.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      hydrateDeferredImage(slide, eager = false) {
        const template = slide?.querySelector('template[data-hh-deferred-product-image]');
        if (!template) return;

        const image = template.content.cloneNode(true);
        if (eager) image.querySelectorAll('img').forEach((element) => element.setAttribute('loading', 'eager'));
        template.replaceWith(image);
        this.deferredImageObserver?.unobserve(slide);
      }

      hydrateSlideAndNeighbors(slide) {
        if (!slide || !this.elements.viewer) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const index = slides.indexOf(slide);
        if (index === -1) return;

        this.hydrateDeferredImage(slides[index], true);
        this.hydrateDeferredImage(slides[index - 1]);
        this.hydrateDeferredImage(slides[index + 1]);
      }

      observeDeferredImages() {
        this.deferredImageObserver?.disconnect();
        this.deferredImageObserver = null;
        if (!this.elements.viewer) return;

        const slides = Array.from(
          this.elements.viewer.querySelectorAll('template[data-hh-deferred-product-image]')
        ).map((template) => template.closest('[data-media-id]'));
        if (!slides.length) return;

        if (!('IntersectionObserver' in window)) {
          slides.forEach((slide) => this.hydrateDeferredImage(slide));
          return;
        }

        const slider = this.elements.viewer.slider || this.elements.viewer.querySelector('[id^="Slider-"]');
        this.deferredImageObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.hydrateDeferredImage(entry.target, true);
            });
          },
          {
            root: this.mql.matches ? null : slider,
            rootMargin: this.mql.matches ? '300px 0px' : '0px 100% 0px 100%',
          }
        );
        slides.forEach((slide) => this.deferredImageObserver.observe(slide));
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.observeDeferredImages();
        this.hydrateSlideAndNeighbors(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      disconnectedCallback() {
        this.deferredImageObserver?.disconnect();
        if (this.mql.addEventListener) {
          this.mql.removeEventListener('change', this.handleDeferredImageMediaQueryChange);
        } else if (this.mql.removeListener) {
          this.mql.removeListener(this.handleDeferredImageMediaQueryChange);
        }
      }
    }
  );
}
